import { useEffect, useMemo, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Terminal from './Terminal'
import { TerminalSession, getService } from '../models/useTerminal'
import useWindowSize from '../hooks/useWindowSize'
import ModalWindow from './ModalWindow'

type Props = {
  session: TerminalSession
  isActive: boolean
  hidden?: boolean
  onClose: () => void
  onExpand: () => void
  onReduce: () => void
  onMinimize: () => void
  onFocus: () => void
  onPositionChange: (position: { x: number; y: number }) => void
  onSizeChange: (size: { width: number; height: number }) => void
}

export default function TerminalWindow({
  session,
  isActive,
  hidden,
  onClose,
  onExpand,
  onReduce,
  onMinimize,
  onFocus,
  onPositionChange,
  onSizeChange,
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { width: viewportWidth, height: viewportHeight } = useWindowSize()
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>(() => {
    const service = getService(session.id)
    return service?.getStatus() ?? 'connecting'
  })

  const isFullscreen = isMobile || session.mode === 'fullscreen'
  const isRouteFullscreen = session.openedFromRoute && session.mode === 'fullscreen'
  const isFramedFullscreen = isFullscreen && !isRouteFullscreen
  const borderColor = !isActive
    ? 'contrast.low'
    : status === 'connected'
      ? 'primary.main'
      : status === 'disconnected'
        ? 'secondary.main'
        : 'action.disabled'

  const handleEntered = () => {
    getService(session.id)?.scheduleFit()
  }

  // Compute a centered initial position on mount when session has no stored position
  const initialPosition = useMemo(() => {
    if (session.position) return session.position
    const fallbackWidth = Math.min(theme.breakpoints.values.lg, Math.max(viewportWidth - 32, 480))
    const fallbackHeight = Math.min(Math.round(viewportHeight * 0.6), Math.max(viewportHeight - 32, 320))
    return {
      x: Math.max((viewportWidth - fallbackWidth) / 2, 16),
      y: Math.max((viewportHeight - fallbackHeight) / 2, 16),
    }
  }, [])

  // Persist computed initial position to the store so fullscreen save/restore always has a value
  useEffect(() => {
    if (!session.position) {
      onPositionChange(initialPosition)
    }
  }, [])

  const resolvedPosition = session.position ?? initialPosition

  return (
    <ModalWindow
      open={!session.closing}
      keepMounted
      hidden={hidden}
      onFocus={onFocus}
      onEntered={handleEntered}
      fullscreen={isFullscreen}
      framedFullscreen={isFramedFullscreen}
      disableOpenAnimation={isRouteFullscreen}
      draggable={!isFullscreen}
      resizable={!isFullscreen}
      position={resolvedPosition}
      size={session.size}
      onSizeChange={onSizeChange}
      onPositionChange={onPositionChange}
      zIndex={session.zIndex}
      color={borderColor}
      sx={{
        height: '100%',
        maxHeight: 'none',
        borderColor,
        backgroundColor: 'transparent',
        padding: 0,
      }}
    >
      <Terminal
        sessionId={session.id}
        isActive={isActive}
        standalone={isRouteFullscreen}
        onStatusChange={setStatus}
        onClose={onClose}
        onExpand={isFullscreen ? undefined : onExpand}
        onReduce={isFullscreen ? onReduce : undefined}
        onMinimize={onMinimize}
        onFocus={onFocus}
        logo={isFullscreen}
      />
    </ModalWindow>
  )
}
